<script setup lang="ts">
import { WidgetBreadcrumbs } from '~/components/widget/breadcrumbs'
import type { IResponseData } from '~/types'
import type { IVacancy } from '~/components/entity/vacancy/types'
import { WidgetOrganizationAsideContacts } from '~/components/widget/organization-aside-contacts'
import { WidgetAboutPageHeader, mapItemToSchemaData } from '~/components/widget/about-page'
import { useSeoFromEntity } from '~/composables/useSeoFromEntity'
import { WidgetVacancyPageHeaderSkeleton } from '~/components/widget/vacancy-page'
import { WidgetReviveAd } from '~/components/widget/revive-ad'
import { getActiveEmploymentTypes } from '~/components/entity/vacancy'

const route = useRoute()
const { t, locale } = useI18n()
const config = useRuntimeConfig()
const organizationSlug = computed(() => {
  return (route.params.bank || route.params.microcredit) as string
})

const vacancyUrl = `/vacancies/${route.params.vacancy}`
const { data } = await useApiWith404<IResponseData<IVacancy>>(() => vacancyUrl, {
  key: `organization-vacancy-${route.params.vacancy}`
})

const vacancy = computed(() => data.value?.data)

const orgType = computed(() => vacancy.value.organization_type)

const baseUrl = config.public.siteUrl
useSeoFromEntity(vacancy, baseUrl, locale.value)

const breadcrumbs = computed(() => {
  const orgSlug = organizationSlug.value
  const orgTitle = vacancy.value?.organization.title

  if (orgType.value === 'microcredit_company') {
    return [
      { title: t('common.microcredits'), url: '/microcredit' },
      {
        title: orgTitle || t('common.microcredit_organization'),
        url: orgSlug ? `/microcredit/${orgSlug}` : '/microcredit'
      },
      {
        title: t('common.vacancies'),
        url: orgSlug ? `/microcredit/${orgSlug}/vacancies` : '/vacancies'
      },
      { title: vacancy.value?.title || t('common.vacancy'), url: vacancy.value?.url }
    ]
  }

  return [
    { title: t('common.banks_of_kyrgyzstan'), url: '/banks' },
    {
      title: orgTitle || t('common.bank'),
      url: orgSlug ? `/bank/${orgSlug}` : '/banks'
    },
    {
      title: t('common.vacancies'),
      url: orgSlug ? `/bank/${orgSlug}/vacancies` : '/vacancies'
    },
    { title: vacancy.value?.title || t('common.vacancy'), url: vacancy.value?.url }
  ]
})

const fitItem = computed(() => mapItemToSchemaData(vacancy.value, t('product.inactive_info')))

const employmentTypes = computed(() => {
  if (!vacancy.value?.employment_type) return []
  return getActiveEmploymentTypes(vacancy.value.employment_type)
})

const formattedSalary = computed(() => {
  if (!vacancy.value?.base_salary || !vacancy.value?.salary_currency) return null
  const currencyId = useGetCurrencyIdByCode(vacancy.value.salary_currency.toLowerCase())
  const validCurrencyId = currencyId || 1
  return useCurrencyFormat(vacancy.value.base_salary, validCurrencyId)
})

watch(
  () => vacancy.value,
  (val) => {
    const expectedSlug = organizationSlug.value
    if (val && val.organization?.slug && expectedSlug && val.organization.slug !== expectedSlug) {
      showError({ statusCode: 404 })
    }
  },
  { immediate: true }
)
</script>

<template>
  <Main>
    <Container>
      <WidgetBreadcrumbs :links="breadcrumbs" />
      <ContentLayout>
        <template #right-sidebar>
          <aside>
            <WidgetOrganizationAsideContacts v-if="vacancy" :organization="vacancy.organization" />
            <WidgetReviveAd zoneid="27" class="overflow-hidden rounded-lg" />
          </aside>
        </template>
        <div>
          <div class="space-y-8" role="main">
            <WidgetAboutPageHeader v-if="vacancy" type="vacancy" :product="fitItem">
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-2">
                <div v-if="vacancy && vacancy.location && vacancy.location.title">
                  <div class="SecondaryTextHeadings">{{ $t('common.location') }}</div>
                  <div class="text-sm font-semibold">{{ vacancy.location.title }}</div>
                </div>
                <div v-if="formattedSalary">
                  <div class="SecondaryTextHeadings">{{ $t('common.salary_label') }}</div>
                  <div class="text-sm font-semibold">{{ formattedSalary }}</div>
                </div>
                <div v-if="employmentTypes.length > 0">
                  <div class="SecondaryTextHeadings">{{ $t('common.employment_type_label') }}</div>
                  <div class="text-sm font-semibold">
                    <span v-for="(type, index) in employmentTypes" :key="type">
                      {{ $t(type) }}<span v-if="index < employmentTypes.length - 1">, </span>
                    </span>
                  </div>
                </div>
              </div>
              <Content>
                <div v-if="vacancy" v-hydrated-html="vacancy.description" />
              </Content>
            </WidgetAboutPageHeader>
            <WidgetVacancyPageHeaderSkeleton v-else />
          </div>
        </div>
      </ContentLayout>
    </Container>
  </Main>
</template>
